import { useMemo } from 'react';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import { isNullOrUndefined } from '~/modules/common/util';
import { ALLOCATION_PERIOD } from '../components/AllocationPeriodTabs';

const dayOfWeekValueMap = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7
};

const DAYS_IN_WEEK = 7;

const getDaysInDateRange = (start, end) =>
  Math.floor(end.diff(start, 'days').days) + 1;

const getPeriodDetails = (entryDate, allocationPeriod) => {
  switch (allocationPeriod) {
    case ALLOCATION_PERIOD.WEEK: {
      const daysFromSunday =
        entryDate.weekday === dayOfWeekValueMap.SUNDAY ? 0 : entryDate.weekday;
      const periodStart = entryDate
        .minus({ days: daysFromSunday })
        .startOf('day');

      return {
        periodStart,
        periodEnd: periodStart.plus({ days: 6 }).endOf('day'),
        daysInFullPeriod: DAYS_IN_WEEK,
        nextPeriodStart: periodStart.plus({ weeks: 1 })
      };
    }
    case ALLOCATION_PERIOD.MONTH:
    default: {
      const periodStart = entryDate.startOf('month');

      return {
        periodStart,
        periodEnd: entryDate.endOf('month'),
        daysInFullPeriod: entryDate.daysInMonth,
        nextPeriodStart: periodStart.plus({ months: 1 })
      };
    }
  }
};

const getTotalHoursAndProratedPeriodDetail = ({
  startDate,
  endDate,
  hoursPerPeriod,
  allocationPeriod
}) => {
  if (
    allocationPeriod === ALLOCATION_PERIOD.ENTIRE_PERIOD ||
    !startDate ||
    !endDate ||
    isNullOrUndefined(hoursPerPeriod)
  ) {
    return { totalHours: undefined, isProratedPeriod: false };
  }

  if (hoursPerPeriod === 0) {
    return { totalHours: 0, isProratedPeriod: false };
  }

  let total = 0;
  let isProratedPeriod = false;
  let currentPeriodStart = startDate;

  while (currentPeriodStart <= endDate) {
    const {
      periodStart,
      periodEnd,
      daysInFullPeriod,
      nextPeriodStart
    } = getPeriodDetails(currentPeriodStart, allocationPeriod);

    const effectiveStart = periodStart < startDate ? startDate : periodStart;
    const effectiveEnd = periodEnd > endDate ? endDate : periodEnd;

    const daysInPeriod = getDaysInDateRange(effectiveStart, effectiveEnd);

    if (daysInPeriod < daysInFullPeriod) {
      isProratedPeriod = true;
      total += (daysInPeriod / daysInFullPeriod) * hoursPerPeriod;
    } else {
      total += hoursPerPeriod;
    }

    currentPeriodStart = nextPeriodStart;
  }

  return { totalHours: roundToDecimals(total), isProratedPeriod };
};

const usePeriodAllocationTotal = ({
  startDate,
  endDate,
  hoursPerPeriod,
  allocationPeriod
}) => {
  const { totalHours, showProrationNote } = useMemo(() => {
    const {
      totalHours: hours,
      isProratedPeriod
    } = getTotalHoursAndProratedPeriodDetail({
      startDate,
      endDate,
      hoursPerPeriod,
      allocationPeriod
    });

    return { totalHours: hours, showProrationNote: isProratedPeriod };
  }, [startDate, endDate, hoursPerPeriod, allocationPeriod]);

  return { totalHours, showProrationNote };
};

export default usePeriodAllocationTotal;
